// AI Studio session tabs (POR-14795) — the postMessage protocol between each
// session and the shell (AiStudioSessions.vue). The main session lives in the
// shell's own window; every extra session is a same-origin iframe rendered in
// "session tab" mode (<meta name="ai-studio-session-tab">).
//
// Message: { ns: 'ai-studio-sessions', dir: 'to-shell' | 'to-session', type, payload }
//   to-shell:   state {conversationId, title, busy} · open-new-tab · focus-conversation {conversationId}
//               · navigate-away {url} · cancelled
//   to-session: sessions {count, openConversationIds, canOpenMore} · cancel · stay

import { BRIDGE_NAMESPACE, isBridgeMessage, leaveThroughShell, resolveSessionTabKey } from './ai-studio-session-tabs';

let cachedSessionTabKey;
let latestShellInfo = null;

function metaContent(name) {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';
}

export function sessionTabKey() {
  if (cachedSessionTabKey === undefined) {
    // Only inside the shell's iframe: the same page opened on its own is a normal
    // page whose navigations must work. (POR-14795)
    cachedSessionTabKey = resolveSessionTabKey(metaContent('ai-studio-session-tab'), window.parent !== window);
  }
  return cachedSessionTabKey;
}

export function isSessionTab() {
  return !!sessionTabKey();
}

// 'new', a conversation id, or '' outside a session tab.
export function sessionTabConversation() {
  return metaContent('ai-studio-session-conversation');
}

function shellWindow() {
  return isSessionTab() ? window.parent : window;
}

function post(target, dir, type, payload) {
  try {
    target.postMessage({ ns: BRIDGE_NAMESPACE, dir, type, payload }, window.location.origin);
  } catch (e) {
    // The other side is gone (closed tab) or there is no shell.
  }
}

export function postToShell(type, payload = {}) {
  post(shellWindow(), 'to-shell', type, payload);
}

export function postToSession(targetWindow, type, payload = {}) {
  if (targetWindow) post(targetWindow, 'to-session', type, payload);
}

export function listenToShell(handler) {
  const onMessage = (event) => {
    if (event.origin !== window.location.origin || event.source !== shellWindow()) return;
    if (!isBridgeMessage(event.data, 'to-session')) return;
    const payload = event.data.payload || {};
    if (event.data.type === 'sessions') latestShellInfo = payload;
    handler(event.data.type, payload);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

export function listenToSessions(handler) {
  const onMessage = (event) => {
    if (event.origin !== window.location.origin) return;
    if (!isBridgeMessage(event.data, 'to-shell')) return;
    handler(event.source, event.data.type, event.data.payload || {});
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

// Every AI Studio navigation that leaves the page goes through here. With other
// sessions open, the shell decides (one may still be generating); otherwise it
// is the plain navigation it always was.
export function leaveAiStudio(url) {
  if (!url) return;
  if (leaveThroughShell(isSessionTab(), latestShellInfo)) {
    postToShell('navigate-away', { url });
    return;
  }
  window.location.href = url;
}
